import config from '@/config'
import { ApiProviderModule } from '@/core/api/ApiProvider'
import { IRoute } from '@/contracts/core/api/interface/IRoute'
import { EnumRouteType } from '@/contracts/core/api/enum/EnumRouteType'
import { ApiResponse } from '@/contracts/core/api/entity/ApiResponse'
import { ApiBytesResponse } from '@/contracts/core/api/entity/ApiBytesResponse'
import { IApi } from '@/contracts/core/api/interface/IApi'
import { isArrayBuffer } from '@/helpers/isArrayBuffer'

export abstract class Route<P, T> implements IRoute<P, T> {
  abstract url(): string;

  abstract type(): EnumRouteType;

  abstract offline(data: P): Promise<ApiResponse<T>>;

  /** По умолчанию — общий флаг API; маршруты модуля переопределяют его настройкой своего модуля */
  isOffline(): boolean {
    return config.api.offline === 'true'
  }

  /** Заголовки двоичных запросов по параметрам (Content-Range, Range); маршрут байтов переопределяет */
  bytesHeaders(params: P): Record<string, string> {
    void params
    return {}
  }

  exec(params: P): Promise<ApiResponse<T>> {
    if (this.isOffline()) {
      return this.offline(params)
    }
    const apiModule: IApi = ApiProviderModule()
    return apiModule.exec(this, params)
  }

  /** Отправить байты (кусок файла) с заголовками bytesHeaders(params) */
  sendBytes(params: P, body: Blob): Promise<ApiResponse<T>> {
    if (this.isOffline()) {
      return this.offline(params)
    }
    const apiModule: IApi = ApiProviderModule()
    return apiModule.sendBytes(this, params, body, this.bytesHeaders(params))
  }

  /** Получить байты (кусок файла) с заголовками bytesHeaders(params); без сервера — из offline(), если там ArrayBuffer */
  async getBytes(params: P): Promise<ApiBytesResponse> {
    if (this.isOffline()) {
      const response = await this.offline(params)
      const data = response.isSuccess() ? response.getData() : null
      if (isArrayBuffer(data)) {
        return new ApiBytesResponse({ success: true, data, size: data.byteLength })
      }
      return new ApiBytesResponse({ success: false, error: response.isSuccess() ? 'bad_response' : response.getError() })
    }
    const apiModule: IApi = ApiProviderModule()
    return apiModule.getBytes(this, params, this.bytesHeaders(params))
  }
}
